import { load } from "cheerio"
import type { Element } from "domhandler"
import type { StaticPageScriptDescriptor } from "@/services/static-page-service/index"

const DEFAULT_ORIGIN_PORTS: Record<string, string> = {
  "http:": "80",
  "https:": "443",
}
const WHITESPACE_REGEX = /\s+/

const normalizeOrigin = (value: string): string | null => {
  try {
    const parsedUrl = new URL(value)
    const protocol = parsedUrl.protocol.toLowerCase()
    const hostname = parsedUrl.hostname.toLowerCase()
    const port = parsedUrl.port || DEFAULT_ORIGIN_PORTS[protocol]

    if (!(protocol && hostname && port)) {
      return null
    }

    return `${protocol}//${hostname}:${port}`
  } catch {
    return null
  }
}

const resolveUrl = (href: string, pageUrl: string): string | null => {
  try {
    return new URL(href, pageUrl).toString()
  } catch {
    return null
  }
}

const isHttpProtocolUrl = (value: string): boolean => {
  try {
    const parsedUrl = new URL(value)
    const protocol = parsedUrl.protocol.toLowerCase()

    return protocol === "http:" || protocol === "https:"
  } catch {
    return false
  }
}

const getScriptDedupeKey = (descriptor: StaticPageScriptDescriptor): string => {
  const sourceKey = descriptor.src
    ? `src:${descriptor.src}`
    : `inline:${descriptor.inlineCode ?? ""}`

  return sourceKey
}

const pushUnique = <T>(
  items: T[],
  seen: Set<string>,
  key: string,
  value: T
): void => {
  if (seen.has(key)) {
    return
  }

  seen.add(key)
  items.push(value)
}

const getConfiguredAllowedAssetOrigins = (): string[] => {
  return (process.env.WP_ALLOWED_ASSET_ORIGINS ?? "")
    .split(",")
    .map((origin) => origin.trim())
    .filter((origin) => origin.length > 0)
}

const getDefaultAllowedAssetOrigins = (pageUrl: string): string[] => {
  return [
    process.env.WP_API_URL ?? "",
    process.env.NEXT_PUBLIC_WP_API_URL ?? "",
    pageUrl,
  ]
}

const hasAttribute = (element: Element, attribute: string): boolean => {
  return Object.hasOwn(element.attribs ?? {}, attribute)
}

const getFirstNonEmptyOuterHtml = (
  fullHtml: string,
  selectors: string[]
): string | null => {
  const document = load(fullHtml)

  for (const selector of selectors) {
    const element = document(selector).first()

    if (!element.length) {
      continue
    }

    const html = document.html(element).trim()

    if (html.length > 0) {
      return html
    }
  }

  return null
}

export const buildAllowedAssetOriginSet = (pageUrl: string): Set<string> => {
  const normalizedOrigins = [
    ...getConfiguredAllowedAssetOrigins(),
    ...getDefaultAllowedAssetOrigins(pageUrl),
  ]
    .map((origin) => normalizeOrigin(origin))
    .filter((origin): origin is string => Boolean(origin))

  return new Set(normalizedOrigins)
}

export const isAllowedAssetUrl = (
  assetUrl: string,
  allowedOrigins: Set<string>
): boolean => {
  const normalizedOrigin = normalizeOrigin(assetUrl)

  if (!normalizedOrigin) {
    return false
  }

  return allowedOrigins.has(normalizedOrigin)
}

export const extractRenderableMarkup = (fullHtml: string): string | null => {
  const document = load(fullHtml)
  const bodyHtml = document("body").first().html()?.trim() ?? null

  return (
    getFirstNonEmptyOuterHtml(fullHtml, ["main", "article"]) ??
    (bodyHtml && bodyHtml.length > 0 ? bodyHtml : null)
  )
}

export const extractStylesheets = (
  fullHtml: string,
  pageUrl: string
): string[] => {
  const document = load(fullHtml)
  const stylesheets: string[] = []
  const seenStylesheets = new Set<string>()
  const allowedOrigins = buildAllowedAssetOriginSet(pageUrl)

  document("link[rel]").each((_, element) => {
    const relValue = (element.attribs?.rel ?? "").toLowerCase().trim()
    const relTokens = relValue
      .split(WHITESPACE_REGEX)
      .filter((token) => token.length > 0)

    if (!relTokens.includes("stylesheet")) {
      return
    }

    const href = element.attribs?.href

    if (!href) {
      return
    }

    const resolvedHref = resolveUrl(href, pageUrl)

    if (!resolvedHref) {
      return
    }

    if (
      !(
        isHttpProtocolUrl(resolvedHref) &&
        isAllowedAssetUrl(resolvedHref, allowedOrigins)
      )
    ) {
      return
    }

    pushUnique(stylesheets, seenStylesheets, resolvedHref, resolvedHref)
  })

  return stylesheets
}

export const extractInlineStyles = (fullHtml: string): string[] => {
  const document = load(fullHtml)
  const inlineStyles: string[] = []
  const seenStyles = new Set<string>()

  document("style").each((_, element) => {
    const css = document(element).html()?.trim() ?? ""

    if (!css) {
      return
    }

    pushUnique(inlineStyles, seenStyles, css, css)
  })

  return inlineStyles
}

const buildScriptBaseDescriptor = (
  element: Element
): StaticPageScriptDescriptor => {
  const type = element.attribs?.type
  const hasAsync = hasAttribute(element, "async")
  const hasDefer = hasAttribute(element, "defer")

  return {
    ...(type ? { type } : {}),
    ...(hasAsync ? { async: true } : {}),
    ...(hasDefer ? { defer: true } : {}),
  }
}

const buildExternalScriptDescriptor = (
  src: string,
  element: Element,
  pageUrl: string,
  allowedOrigins: Set<string>
): { key: string; descriptor: StaticPageScriptDescriptor } | null => {
  const resolvedSrc = resolveUrl(src, pageUrl)

  if (!(resolvedSrc && isAllowedAssetUrl(resolvedSrc, allowedOrigins))) {
    return null
  }

  return {
    key: getScriptDedupeKey({
      ...buildScriptBaseDescriptor(element),
      src: resolvedSrc,
    }),
    descriptor: {
      ...buildScriptBaseDescriptor(element),
      src: resolvedSrc,
    },
  }
}

const buildInlineScriptDescriptor = (
  inlineCode: string,
  element: Element
): { key: string; descriptor: StaticPageScriptDescriptor } | null => {
  if (!inlineCode) {
    return null
  }

  return {
    key: getScriptDedupeKey({
      ...buildScriptBaseDescriptor(element),
      inlineCode,
    }),
    descriptor: {
      ...buildScriptBaseDescriptor(element),
      inlineCode,
    },
  }
}

export const extractScripts = (
  fullHtml: string,
  pageUrl: string
): StaticPageScriptDescriptor[] => {
  const document = load(fullHtml)
  const scripts: StaticPageScriptDescriptor[] = []
  const seenScripts = new Set<string>()
  const allowedOrigins = buildAllowedAssetOriginSet(pageUrl)

  document("script").each((_, element) => {
    const src = element.attribs?.src
    const inlineCode = document(element).html()?.trim() ?? ""
    const scriptEntry = src
      ? buildExternalScriptDescriptor(src, element, pageUrl, allowedOrigins)
      : buildInlineScriptDescriptor(inlineCode, element)

    if (!scriptEntry) {
      return
    }

    pushUnique(scripts, seenScripts, scriptEntry.key, scriptEntry.descriptor)
  })

  return scripts
}
